/**
 * NMBLR brand mark. Served as a static asset from public/logo.png. The URL is
 * built from Vite's BASE_URL so the image resolves whether the SPA is mounted
 * at root (this portal) or under a path prefix. Replacing public/logo.png swaps
 * the mark everywhere without a rebuild.
 *
 * The `size` prop sets the rendered height in pixels; width scales
 * automatically to preserve the asset's 77:40 aspect ratio.
 */
const LOGO_ASPECT = 77 / 40;

/**
 * `gradient`: paints the brand ramp (`--nmblr-gradient-mark`, left→right
 * green→teal→cyan→blue) *through* the logo's silhouette via a CSS mask, so the
 * mark renders as a single gradient fill. Requires logo.png to be a transparent
 * silhouette (an opaque background would show as a gradient rectangle instead).
 */
export function NmblrMark({
  size = 14,
  className = '',
  gradient = false,
}: {
  size?: number;
  className?: string;
  gradient?: boolean;
}) {
  const height = size;
  const width = Math.round(height * LOGO_ASPECT);
  const logoUrl = `${import.meta.env.BASE_URL}logo.png`;

  if (gradient) {
    const maskUrl = `url("${logoUrl}")`;
    return (
      <span
        role="img"
        aria-label="NMBLR"
        className={className}
        style={{
          display: 'inline-block',
          width,
          height,
          background: 'var(--nmblr-gradient-mark)',
          WebkitMaskImage: maskUrl,
          maskImage: maskUrl,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
        }}
      />
    );
  }

  return (
    <img
      src={logoUrl}
      width={width}
      height={height}
      alt="NMBLR"
      className={className}
      style={{ objectFit: 'contain' }}
    />
  );
}
